// ─── Train Animation Engine ──────────────────────────────────────────────────
// Self-contained train animation that runs on its own Leaflet layer group.
// Does NOT share layers with MapView — immune to clearLayers() redraws.
//
// Usage:
//   const engine = new TrainEngine(map);
//   engine.setCoords(polyline);   // updates route, auto-starts
//   engine.stop();                // pauses
//   engine.destroy();             // full cleanup

import L from "leaflet";
import { TRAIN_ICON_DIRECTIONS } from "../data/design-tokens";

// ─── Settings Key ────────────────────────────────────────────────────────────

const LS_KEY = "via-fera-train-animation";

/** Read user preference. Default: enabled. */
export function isTrainEnabled(): boolean {
  try {
    return localStorage.getItem(LS_KEY) !== "false";
  } catch {
    return true;
  }
}

/** Persist user preference. */
export function setTrainEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(LS_KEY, enabled ? "true" : "false");
  } catch { /* quota / incognito */ }
}

// ─── Train Icon ──────────────────────────────────────────────────────────────

function getDirection(angle: number): keyof typeof TRAIN_ICON_DIRECTIONS {
  const normalized = ((angle % 360) + 360) % 360;
  if (normalized >= 337.5 || normalized < 22.5) return 'N';
  if (normalized >= 22.5 && normalized < 67.5) return 'NE';
  if (normalized >= 67.5 && normalized < 112.5) return 'E';
  if (normalized >= 112.5 && normalized < 157.5) return 'SE';
  if (normalized >= 157.5 && normalized < 202.5) return 'S';
  if (normalized >= 202.5 && normalized < 247.5) return 'SW';
  if (normalized >= 247.5 && normalized < 292.5) return 'W';
  if (normalized >= 292.5 && normalized < 337.5) return 'NW';
  return 'N';
}

function createTrainIcon(dir: keyof typeof TRAIN_ICON_DIRECTIONS = 'N'): L.DivIcon {
  const imgUrl = TRAIN_ICON_DIRECTIONS[dir];
  const portrait = dir === 'N' || dir === 'S';
  const size: [number, number]   = portrait ? [42, 80] : [80, 42];
  const anchor: [number, number] = portrait ? [21, 40] : [40, 21];
  return L.divIcon({
    className: "train-engine-marker cursor-grab active:cursor-grabbing",
    html: `<div style="filter:drop-shadow(0 2px 6px rgba(0,0,0,0.5));pointer-events:none;width:100%;height:100%;"><img src="${imgUrl}" style="width:100%;height:100%;object-fit:contain;" /></div>`,
    iconSize: size,
    iconAnchor: anchor,
  });
}

// ─── Mercator Interpolation ──────────────────────────────────────────────────

/** Pre-compute projected points + cumulative distances for O(1) lookups. */
interface ProjectedRoute {
  coords: [number, number][];
  points: L.Point[];
  cumDist: number[];     // cumDist[i] = distance from start to points[i]
  totalDist: number;
}

/** Deduplicate consecutive identical coords that would create zero-length segments. */
function dedupeCoords(coords: [number, number][]): [number, number][] {
  if (coords.length === 0) return [];
  const out: [number, number][] = [coords[0]];
  for (let i = 1; i < coords.length; i++) {
    if (coords[i][0] !== coords[i - 1][0] || coords[i][1] !== coords[i - 1][1]) {
      out.push(coords[i]);
    }
  }
  return out;
}

function projectRoute(coords: [number, number][]): ProjectedRoute | null {
  const deduped = dedupeCoords(coords);
  if (deduped.length < 2) return null;

  const points = deduped.map(c =>
    L.Projection.SphericalMercator.project(L.latLng(c[0], c[1]))
  );
  const cumDist: number[] = [0];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += points[i - 1].distanceTo(points[i]);
    cumDist.push(total);
  }

  // All points project to the same spot — no traversable route
  if (total === 0) return null;

  return { coords: deduped, points, cumDist, totalDist: total };
}

/** Interpolate position and angle along projected route at progress [0..1]. */
function interpolate(route: ProjectedRoute, progress: number): { pos: [number, number]; angle: number } | null {
  const { points, cumDist, totalDist, coords } = route;
  if (points.length < 2 || totalDist === 0) return { pos: coords[0] ?? [0,0], angle: 0 };

  const target = Math.max(0, Math.min(1, progress)) * totalDist;

  // Binary search for the segment containing target distance
  let lo = 0, hi = cumDist.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (cumDist[mid] <= target) lo = mid; else hi = mid;
  }

  const segLen = cumDist[hi] - cumDist[lo];
  const t = segLen > 0 ? (target - cumDist[lo]) / segLen : 0;

  const x = points[lo].x + (points[hi].x - points[lo].x) * t;
  const y = points[lo].y + (points[hi].y - points[lo].y) * t;

  // Guard against NaN from degenerate geometry
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  const ll = L.Projection.SphericalMercator.unproject(L.point(x, y));
  
  const dx = points[hi].x - points[lo].x;
  const dy = points[hi].y - points[lo].y;
  const angleDeg = Math.atan2(dy, dx) * (180 / Math.PI);
  const angle = (angleDeg + 90 + 360) % 360;

  return { pos: [ll.lat, ll.lng], angle };
}

function getClosestPointOnSegment(p: L.Point, a: L.Point, b: L.Point): { point: L.Point, t: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { point: a, t: 0 }; 

  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t)); 

  return {
    point: new L.Point(a.x + t * dx, a.y + t * dy),
    t
  };
}

function snapToRoute(latlng: L.LatLng, route: ProjectedRoute): { linearProgress: number, angle: number } {
  const p = L.Projection.SphericalMercator.project(latlng);
  
  let minDist = Infinity;
  let bestLinearProgress = 0;
  let bestAngle = 0;

  for (let i = 0; i < route.points.length - 1; i++) {
    const a = route.points[i];
    const b = route.points[i + 1];
    
    const { point: c, t } = getClosestPointOnSegment(p, a, b);
    const dist = p.distanceTo(c);
    
    if (dist < minDist) {
      minDist = dist;
      const segLen = route.cumDist[i + 1] - route.cumDist[i];
      const distAlongRoute = route.cumDist[i] + t * segLen;
      bestLinearProgress = distAlongRoute / route.totalDist;

      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const angleDeg = Math.atan2(dy, dx) * (180 / Math.PI);
      bestAngle = (angleDeg + 90 + 360) % 360;
    }
  }

  if (route.totalDist === 0) bestLinearProgress = 0;
  return { linearProgress: bestLinearProgress, angle: bestAngle };
}

// ─── Easing ──────────────────────────────────────────────────────────────────

/** Smooth ease-in-out: slow at endpoints, full speed in the middle. */
function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

// ─── Engine ──────────────────────────────────────────────────────────────────

export class TrainEngine {
  private readonly layer: L.LayerGroup;
  private marker: L.Marker | null = null;
  private route: ProjectedRoute | null = null;
  private animId: number | null = null;
  
  private lastTickTime = 0;
  private progressTime = 0;
  private duration = 60000;
  
  private running = false;
  private dragging = false;
  private destroyed = false;
  private pausedByUser = false;
  private resetting = false;       // true during end-of-loop pause+fade
  private resetTimer: ReturnType<typeof globalThis.setTimeout> | null = null;

  private currentDir: keyof typeof TRAIN_ICON_DIRECTIONS = 'N';

  private readonly onVisChange = (): void => {
    if (document.hidden) {
      this.stopLoop();
    } else if (this.route && this.marker && !this.destroyed && !this.dragging && !this.pausedByUser && !this.resetting && this.progressTime < this.duration) {
      this.running = true;
      this.lastTickTime = performance.now();
      this.tick();
    }
  };

  constructor(map: L.Map) {
    this.layer = L.layerGroup().addTo(map);
    document.addEventListener("visibilitychange", this.onVisChange);
  }

  /** Set (or replace) the polyline coords. Auto-starts if running or first call. */
  setCoords(coords: [number, number][]): void {
    if (this.destroyed) return;

    this.stopLoop();
    this.removeMarker();

    this.route = projectRoute(coords);
    if (!this.route) return;

    // Duration: ~120s baseline, scaled by route complexity, capped at 4 min
    this.duration = Math.min(240_000, Math.max(120_000, this.route.coords.length * 600));
    this.progressTime = 0;

    const initialPos = this.route.coords[0];
    const initialInterp = interpolate(this.route, 0);
    this.currentDir = getDirection(initialInterp ? initialInterp.angle : 0);

    // Create marker at the start
    this.marker = L.marker(initialPos, {
      icon: createTrainIcon(this.currentDir),
      interactive: true,
      draggable: true,
      zIndexOffset: 2000,
    });
    
    this.marker.on('click', () => {
      if (this.running) {
        this.stop();
      } else {
        if (this.progressTime >= this.duration) {
          this.progressTime = 0;
        }
        this.start();
      }
    });

    this.marker.on('dragstart', () => {
      this.dragging = true;
      this.stopLoop();
    });

    this.marker.on('drag', () => {
      if (!this.route || !this.marker) return;
      const { linearProgress, angle } = snapToRoute(this.marker.getLatLng(), this.route);
      
      const clampLinear = Math.max(0, Math.min(1, linearProgress));
      const rawProgress = Math.acos(1 - 2 * clampLinear) / Math.PI;
      this.progressTime = rawProgress * this.duration;
      
      const nextDir = getDirection(angle);
      if (nextDir !== this.currentDir) {
        this.currentDir = nextDir;
        this.marker.setIcon(createTrainIcon(this.currentDir));
      }
      
      const interp = interpolate(this.route, easeInOutSine(rawProgress));
      if (interp) {
        this.marker.setLatLng(interp.pos);
      }
    });

    this.marker.on('dragend', () => {
      this.dragging = false;
      if (this.progressTime < this.duration) {
        this.start();
      }
    });

    this.layer.addLayer(this.marker);

    this.start();
  }

  /** Begin animation loop. */
  start(): void {
    if (this.destroyed || this.running || this.resetting || !this.route || this.dragging) return;
    this.pausedByUser = false;
    if (this.progressTime >= this.duration) this.progressTime = 0;
    this.running = true;
    this.lastTickTime = performance.now();
    this.tick();
  }

  /** Pause animation (marker stays on map). */
  stop(): void {
    this.pausedByUser = true;
    this.stopLoop();
    // Ensure marker is fully visible if stopped mid-fade
    const el = this.marker?.getElement() as HTMLElement | null;
    if (el) { el.style.transition = ""; el.style.opacity = "1"; }
  }

  /** Full cleanup — removes layer group from map. Safe to call multiple times. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopLoop();
    document.removeEventListener("visibilitychange", this.onVisChange);
    this.removeMarker();
    this.layer.remove();
    this.route = null;
  }

  // ── Private ────────────────────────────────────────────────────────

  private stopLoop(): void {
    this.running = false;
    if (this.animId !== null) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
    if (this.resetTimer !== null) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
      this.resetting = false;
    }
  }

  /** End-of-loop: pause 1s → fade out → reappear at start → animate again. */
  private resetLoop(): void {
    if (this.destroyed || !this.marker || !this.route) return;
    this.resetting = true;

    const el = this.marker.getElement() as HTMLElement | null;
    // Pause 1s at the end, then fade out over 0.5s
    this.resetTimer = globalThis.setTimeout(() => {
      if (this.destroyed || this.pausedByUser || !this.marker) { this.resetting = false; return; }
      if (el) {
        el.style.transition = "opacity 0.5s ease";
        el.style.opacity = "0";
      }
      // After fade-out: teleport to start, fade in, restart
      this.resetTimer = globalThis.setTimeout(() => {
        if (this.destroyed || this.pausedByUser || !this.marker || !this.route) { this.resetting = false; return; }
        this.progressTime = 0;
        this.updatePosition();
        if (el) {
          el.style.transition = "opacity 0.4s ease";
          el.style.opacity = "1";
        }
        this.resetting = false;
        // Small delay so the fade-in is visible before motion starts
        this.resetTimer = globalThis.setTimeout(() => {
          if (!this.destroyed && !this.pausedByUser) this.start();
        }, 500);
      }, 500);
    }, 1000);
  }

  private removeMarker(): void {
    if (this.marker) {
      this.marker.off('click');
      this.marker.off('dragstart');
      this.marker.off('drag');
      this.marker.off('dragend');
      this.layer.removeLayer(this.marker);
      this.marker = null;
    }
  }

  private readonly tick = (): void => {
    if (!this.running || !this.route || !this.marker || this.destroyed || this.dragging) return;

    const now = performance.now();
    const dt = now - this.lastTickTime;
    this.lastTickTime = now;
    
    this.progressTime += dt;
    
    if (this.progressTime >= this.duration) {
      this.progressTime = this.duration;
      this.updatePosition();
      this.running = false;
      if (!this.pausedByUser) this.resetLoop();
      return;
    }

    this.updatePosition();

    // Gentle sway & bob (train "clacking" on tracks)
    const el = this.marker.getElement() as HTMLDivElement | null;
    const inner = el?.firstElementChild as HTMLElement | null;
    if (inner) {
      const phase = (this.progressTime / 600) % 1; // one cycle every 600ms
      const sway = Math.sin(phase * Math.PI * 2) * 0.5;  // ±0.5px lateral
      const bob = Math.abs(Math.sin(phase * Math.PI)) * 0.4; // 0–0.4px vertical
      inner.style.transform = `translate(${sway}px, ${-bob}px)`;
    }

    this.animId = requestAnimationFrame(this.tick);
  };
  
  private updatePosition(): void {
    if (!this.route || !this.marker) return;
    
    const rawProgress = Math.max(0, Math.min(1, this.progressTime / this.duration));
    const progress = easeInOutSine(rawProgress);

    const interp = interpolate(this.route, progress);
    if (!interp) return;
    
    this.marker.setLatLng(interp.pos);
    
    const nextDir = getDirection(interp.angle);
    if (nextDir !== this.currentDir) {
      this.currentDir = nextDir;
      this.marker.setIcon(createTrainIcon(this.currentDir));
    }
  }
}
